export const navigation = {
  tabs: {
    home: 'Home',
    assets: 'Assets',
    work: 'Work',
    reports: 'Reports',
    contacts: 'Contacts',
    analytics: 'Insights',
    account: 'Account',
  },
  planLapsed: {
    member: "This workspace's plan no longer includes Teams. You can read everything but not change it — ask your workspace owner to restore the plan.",
  },
  mobile: {
    moreTab: 'More',
    scanTab: 'Scan',
    moreSubtitle: "More of your workspace, and shortcuts.",
    planLockedA11y: "{{label}}, not in your plan. Opens the plans that include it.",
    overflowSection: 'Shortcuts',
    overflow: {
      sites: "Sites",
      contacts: 'Contacts',
      reports: 'Reports',
      analytics: 'Insights',
      knowledge: 'Knowledge',
      guide: "Help",
      assistant: 'Assistant',
      activity: 'Activity',
      calendar: 'Calendar',
      checklists: "Checklists",
      groupRules: "Group rules",
      compliance: 'Compliance',
      inspections: 'Inspections',
      jobs: 'Jobs',
      people: 'People',
      profile: 'Profile',
    },
  },
  stack: {
    forgotPassword: 'Forgot Password',
    setNewPassword: 'Set New Password',
    newAsset: 'New Asset',
    editAsset: 'Edit asset',
    addDocument: 'Add Document',
    renewDocument: 'Renew Document',
    newChecklist: 'New Checklist',
    newInspection: 'New Inspection',
    shareReport: "Send report",
    convertToJobs: 'Create Jobs',
    inspectionReview: 'Inspection Review',
    contractors: 'Contractors',
    contractorHome: 'Contractor Home',
    scheduleInspection: 'Schedule Inspection',
    scheduleDetails: 'Schedule Details',
    // ONE name for one destination: `/schedules` and `/(tabs)/calendar` are the
    // same screen, and it lists scheduled JOBS as well as inspections. The More
    // menu already called it "Calendar" (navigation.mobile.overflow.calendar);
    // the stack title used to say "Scheduled Inspections", which was both a
    // second name and — since jobs appear here — untrue.
    calendar: 'Calendar',
    teamManagement: 'Team Management',
    teamInvitation: 'Team Invitation',
    workspaceAiAudit: 'Workspace AI Audit',
    workspaceBilling: 'Billing & usage',
    contactInvitation: 'Contact invitation',
    asset: 'Asset',
    submitQuote: 'Submit quote',
    // Split out of the joined subtitles above so each fact can be its own pill.
    inviteOnly: 'Invite-only',
    privateRequest: 'Private request',
    quoteRequest: 'Quote request',
    // M3 (IA pass): unified noun — an inspection IS its report when completed.
    // Format makes the lifecycle state visible without inventing a new object
    // type. Previous "Inspection report" suggested a separate document; this
    // reads as "this inspection's report view".
    // A noun phrase, not a breadcrumb. 'Inspection · Report' truncated to
    // "Inspection …" in the phone header, and every other locale already ships
    // a single phrase (Inspektionsbericht / Informe de inspección / Rapport
    // d'inspection) rather than two words joined by a separator.
    inspectionReport: 'Inspection report',
    checklist: 'Checklist',
    quotes: 'Quotes',
    jobsCreated: 'Jobs created',
    reviewContact: 'Review contact',
    reviewContactSub: 'Edit and add roles',
    inspectionComplete: 'Inspection complete',
    requestQuotes: 'Request quotes',
    generateWithAI: 'Generate with AI',
    generateWithAISub: 'Suggest a checklist draft',
    generationFailed: 'Generation failed',
    reviewAssign: 'Review & assign',
    reviewAssignListSub_one: '{{count}} job · Set work path and assign',
    reviewAssignListSub_other: '{{count}} jobs · Set work path and assign',
    importContacts: 'Import contacts',
    importContactsSub: 'How do you want to add them?',
    phonePicker: 'Phone picker',
    phonePickerSub: 'Add one contact per pick',
    suggestedJobs: 'Follow-up jobs',
    saveWorkflowTemplate: 'Save workflow checklist',
    sentForApproval: 'Sent for approval',
    assetReadOnlyShared: 'Read-only · Shared with you',
    analytics: 'Insights',
    analyticsScope: 'Scope: {{label}}',
    scheduledInspection: 'Scheduled inspection',
    scheduleFallback: 'Schedule',
    sharedKnowledge: 'Shared knowledge',
    settings: 'Settings',
    help: 'Help',
    preferences: 'Preferences',
    deleteAccount: 'Delete account',
    contact: 'Contact',
    addContact: 'Add Contact',
    editContact: 'Edit Contact',
    contractorDirectory: 'Contractor Directory',
    importFromContacts: 'Import from Contacts',
    addContractor: 'Add Contractor',
    alreadyExists: 'Already Exists',
    updateContractor: 'Update Contractor',
    // M3 (IA pass): paired with `inspectionReport`, which is now
    // "Inspection · Report". In-progress reads as "Inspection · In progress"
    // to make the lifecycle state visible alongside the same noun. Same
    // object, different state — never a different name.
    // Just the noun, matching every other locale — ar/de/es/fr all shipped
    // 'Inspection' without a status, which is the tell that the suffix never
    // belonged in a nav title. 'Inspection · In progress' also titles this
    // screen's loading and error states, and stays "In progress" after the
    // inspection is paused or completed. The live status is in the runner bar.
    inspection: 'Inspection',
    job: 'Job',
    convertToJobsApproved_one: "{{count}} approved",
    convertToJobsApproved_other: "{{count}} approved",
    recurringJobCreate: "New maintenance plan",
  },
  scopeChip: {
    all: 'All',
    personal: 'Personal',
    teamNamed: 'Team: {{name}}',
    teamFallback: 'Team',
    modalTitle: 'Scope',
    cancel: 'Cancel',
    a11y: 'Workspace filter, {{label}}',
  },
  shell: {
    brandTitle: 'AssetWRX',
    brandSubtitle: 'Asset Operations Platform',
    navSection: 'Navigation',
    sectionOperate: 'Operate',
    sectionNetwork: 'Network',
    sectionInsights: 'Insights',
    sectionAccount: 'Account',
    brandHomeA11y: 'AssetWRX home',
    brandHomeWithWorkspaceA11y: '{{workspace}} · AssetWRX home',
    collapseNav: 'Collapse navigation',
    expandNav: 'Expand navigation',
    primaryNavA11y: 'Primary navigation',
    accountA11y: 'Account',
    notificationsA11y: 'Notifications',
    skipToContent: 'Skip to main content',
    notificationsA11yUnread_one: "Notifications, {{count}} unread",
    notificationsA11yUnread_other: "Notifications, {{count}} unread",
    notificationsA11yUnreadMany: 'Notifications, more than 99 unread',
    activitySheetTitle: "Notifications",
    activityUnreadSubtitle_one: "{{count}} unread",
    activityUnreadSubtitle_other: "{{count}} unread",
    // The sheet loads the latest notifications only: at that cap there may be more.
    activityUnreadSubtitleCapped: "{{limit}}+ unread",
    activityEmpty: 'No notifications yet',
    activitySeeAll: 'See all activity',
    accountRailA11y: 'Settings sections',
    accountRailTitle: 'Settings',
    accountRailSubtitle: 'Personal account, workspace, and administration',
    sectionPersonal: 'You',
    sectionWorkspace: 'Workspace',
    sectionTeam: 'Team',
    sectionAdmin: 'Administration',
    accountNavProfile: 'Profile',
    accountNavPersonalDetails: 'Profile details',
    accountNavPreferences: 'Preferences',
    accountNavHelp: 'Help',
    accountNavWorkspaceOverview: 'Overview',
    accountNavDefaults: 'Defaults',
    accountNavOrganization: 'Organization',
    accountNavBranding: 'Report branding',
    accountNavTeam: 'Team & invites',
    accountNavBilling: 'Billing & usage',
    accountNavIntegrations: 'Integrations',
    accountNavSyncCenter: "Unsent changes",
    accountNavApiExplorer: 'API explorer',
    accountNavAudit: 'AI & automation audit',
    accountNavTemplates: "Checklists",
    accountNavFeatures: "Features",
    accountNavDiagnostics: "Diagnostics",
    accountNavAbout: "About",
    accountNavIndustryPacks: 'Industry packs',
    accountNavAssetTypes: 'Asset types',
    accountNavAi: 'AI usage audit',
    accountNavAssistantActivity: 'Assistant activity',
    accountNavPrivacy: 'Privacy',
    accountNavBillingShort: 'Billing',
    accountNavTeams: 'Teams',
    accountNavSecurity: 'Security',
    accountNavNotifications: 'Notifications',
    accountNavBrandingDefaults: 'Branding and defaults',
    accountNavHandbook: 'Integration handbook',
    sectionAdvanced: 'Advanced',
    railBackToSettings: 'All settings',
    railAssistant: 'Assistant',
    railIntegrations: 'Automations',
    railBilling: 'Billing',
  },
  // The header trail on detail screens: Site › Asset › work item › Report.
  trail: {
    sites: 'Sites',
    site: 'Site',
    assets: 'Assets',
    asset: 'Asset',
    inspection: 'Inspection',
    job: 'Job',
    report: 'Report',
    plan: 'Plan',
    document: 'Document',
    contacts: 'Contacts',
    contact: 'Contact',
  },
} as const;
